import { HOTKEY_CATEGORIES } from "../hotkeys/hotkeyRegistry"
import type { IHotkeyMeta, THotkeyCategory } from "../hotkeys/hotkeyRegistry"

// One tab of the help panel: the category's copy plus the shortcuts listed under it, in registry
// order.
export type TShortcutGroup = {
	id: THotkeyCategory
	label: string
	hotkeys: IHotkeyMeta[]
}

// Split a registry `label` into the key chips the panel renders. The outer array holds the
// ALTERNATIVE spellings of one shortcut (`"Delete / ⌫"` is one action on two physical keys, and
// carries an aliasCombo for exactly that reason); the inner array holds the keys pressed together
// (`"Shift + Tab"`).
//
// Parsing the existing `label` keeps the registry the single source of truth: a per-entry `keys`
// field would be a second copy of the same fact, free to drift from the label the tooltips already
// show.
export const parseHotkeyLabel = (label: string, isApplePlatform = false): string[][] =>
	label
		.split("/")
		.map((alternative) =>
			alternative
				.split("+")
				.map((key) => toPlatformKey(key.trim(), isApplePlatform))
				.filter(Boolean),
		)
		.filter((keys) => keys.length > 0)

// What a modifier is CALLED on the machine reading the panel. The registry writes one label, in the
// Windows/Linux spelling; on a Mac the same physical keys are printed as glyphs, and a panel that
// says "Ctrl" while the working shortcut is ⌘C is simply wrong for half the team.
//
// "Ctrl" maps to ⌘ because every Ctrl-labelled shortcut in the registry also binds Meta, which is
// the ⌘ key — a registry test pins that pairing, so an entry bound to Control alone cannot quietly
// start rendering as a ⌘ shortcut that does nothing.
const MAC_KEY_GLYPHS: Record<string, string> = {
	Ctrl: "⌘",
	Alt: "⌥",
	Shift: "⇧",
}

const toPlatformKey = (key: string, isApplePlatform: boolean): string =>
	isApplePlatform ? (MAC_KEY_GLYPHS[key] ?? key) : key

// Whether the panel should use the Mac spellings. `userAgentData.platform` where the browser has it,
// falling back to the deprecated-but-universal `platform`, then the UA string; anything unreadable
// (or a non-browser context) reads as "not a Mac", which is the safe default — the registry's own
// labels are already the non-Mac ones.
export const isApplePlatform = (): boolean => {
	if (typeof navigator === "undefined") return false
	const platform =
		(navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
		navigator.platform ??
		navigator.userAgent ??
		""
	return /mac|iphone|ipad|ipod/i.test(platform)
}

// Bucket the registry into the panel's tabs. Categories keep their declared order (the design's),
// and one with no shortcuts is dropped rather than rendered as an empty tab — so a category can be
// declared ahead of the shortcuts that will fill it.
export const groupHotkeysByCategory = (hotkeys: IHotkeyMeta[]): TShortcutGroup[] =>
	HOTKEY_CATEGORIES.map(({ id, label }) => ({
		id,
		label,
		hotkeys: hotkeys.filter((hotkey) => hotkey.category === id),
	})).filter((group) => group.hotkeys.length > 0)
